import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { TREATMENT_CATEGORIES } from "../data/site";
import { Reveal, Eyebrow } from "../components/Reveal";
import { MarqueeStrip } from "../components/MarqueeStrip";
import { TreatmentList } from "../components/TreatmentList";
import { SkinConditions } from "../components/SkinConditions";
import { CTABand } from "../components/CTABand";

export default function Treatments() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "center" }), 400);
    }
  }, [hash]);

  return (
    <main data-testid="treatments-page">
      <section className="border-b border-border bg-secondary/40 pb-16 pt-40 sm:pb-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal y={20}>
            <Eyebrow>Aesthetic Treatments</Eyebrow>
            <h1 className="mt-4 max-w-3xl font-display text-5xl font-light leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl">
              Every treatment, <em className="text-primary">explained.</em>
            </h1>
            <p className="mt-6 max-w-xl text-base font-light leading-relaxed text-muted-foreground">
              100% non-surgical procedures with honest pricing, realistic downtime and no pressure.
              Open any treatment to see exactly what's involved — then book your free consultation.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-10 flex flex-wrap gap-2.5" data-testid="treatments-category-nav">
              {TREATMENT_CATEGORIES.map((c) => (
                <a
                  key={c.id}
                  href={`#cat-${c.id}`}
                  data-testid={`category-nav-${c.id}`}
                  className="border border-border bg-background px-4 py-2 text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 hover:border-primary hover:text-primary"
                >
                  {c.label}
                </a>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {TREATMENT_CATEGORIES.map((c, idx) => (
        <section
          key={c.id}
          id={`cat-${c.id}`}
          className={`scroll-mt-24 py-16 sm:py-20 ${idx % 2 === 1 ? "bg-secondary/30" : ""}`}
          data-testid={`treatment-category-${c.id}`}
        >
          <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <Reveal>
              <div className="flex items-baseline gap-5">
                <span className="font-display text-lg italic text-primary/50">{String(idx + 1).padStart(2, "0")}</span>
                <h2 className="font-display text-3xl font-light tracking-tight sm:text-4xl">{c.label}</h2>
              </div>
              <p className="mt-3 max-w-2xl text-sm font-light leading-relaxed text-muted-foreground sm:text-base">
                {c.description}
              </p>
            </Reveal>
            <div className="mt-6">
              <TreatmentList category={c.id} />
            </div>
            {c.faq?.length > 0 && (
              <div className="mt-10 border border-border bg-background p-8 sm:p-10" data-testid={`category-faq-${c.id}`}>
                <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-primary">
                  Common questions about {c.label.toLowerCase()}
                </p>
                <dl className="mt-5 grid grid-cols-1 gap-x-12 gap-y-6 md:grid-cols-2">
                  {c.faq.map((f) => (
                    <div key={f.q}>
                      <dt className="text-sm font-medium">{f.q}</dt>
                      <dd className="mt-1.5 text-sm font-light leading-relaxed text-muted-foreground">{f.a}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
          </div>
        </section>
      ))}

      <SkinConditions />

      <MarqueeStrip />
      <CTABand />
    </main>
  );
}
